/**
 * DepartmentProgressChart Component
 * Horizontal bar chart showing OKR progress by department
 *
 * Story 2.3: OKR Progress by Department Chart
 */

import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { clsx } from 'clsx';
import { SortAsc, SortDesc } from 'lucide-react';
import { Card, Button } from '../../common';

export interface DepartmentProgress {
  department: string;
  /** Department uuid, or null for the "No department" row. */
  departmentId: string | null;
  okrCount: number;
  averageProgress: number;
  completionRate: number;
}

export interface DepartmentProgressChartProps {
  /** Department progress data */
  data: DepartmentProgress[];
  /** Title for the chart */
  title?: string;
  /** Click handler for department bar; null for the "No department" row */
  onDepartmentClick?: (departmentId: string | null) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Chart height in pixels */
  height?: number;
  /** Additional class names */
  className?: string;
}

type SortBy = 'progress' | 'okrCount' | 'alphabetical';
type SortOrder = 'asc' | 'desc';

/**
 * Get bar color based on progress value
 */
function getProgressColor(progress: number): string {
  if (progress >= 70) return '#22c55e'; // green
  if (progress >= 40) return '#f59e0b'; // amber
  return '#ef4444'; // red
}

/**
 * Custom tooltip content
 */
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as DepartmentProgress;
    return (
      <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-stone-200">
        <div className="font-medium text-stone-900 mb-2">{data.department}</div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-stone-500">OKR Count:</span>
            <span className="font-medium text-stone-900">{data.okrCount}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-stone-500">Avg Progress:</span>
            <span className="font-medium" style={{ color: getProgressColor(data.averageProgress) }}>
              {data.averageProgress}%
            </span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-stone-500">Completion Rate:</span>
            <span className="font-medium text-stone-900">{data.completionRate}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

/**
 * Loading skeleton for the chart
 */
const ChartSkeleton = ({ height }: { height: number }) => (
  <div className="flex items-end gap-2" style={{ height }}>
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="flex-1 flex flex-col justify-end">
        <div
          className="bg-stone-200 animate-pulse rounded-t"
          style={{ height: `${30 + Math.random() * 60}%` }}
        />
      </div>
    ))}
  </div>
);

/**
 * Department Progress Bar Chart Component
 */
export const DepartmentProgressChart: React.FC<DepartmentProgressChartProps> = ({
  data,
  title = 'Progress by Department',
  onDepartmentClick,
  isLoading = false,
  height = 300,
  className,
}) => {
  const [sortBy, setSortBy] = useState<SortBy>('progress');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Sort data based on current sort settings
  const sortedData = [...data].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'progress':
        comparison = a.averageProgress - b.averageProgress;
        break;
      case 'okrCount':
        comparison = a.okrCount - b.okrCount;
        break;
      case 'alphabetical':
        comparison = a.department.localeCompare(b.department);
        break;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const handleSortChange = (newSortBy: SortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder(newSortBy === 'alphabetical' ? 'asc' : 'desc');
    }
  };

  const SortIcon = sortOrder === 'asc' ? SortAsc : SortDesc;

  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">{title}</h3>
        <ChartSkeleton height={height} />
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">{title}</h3>
        <div className="flex flex-col items-center justify-center py-12 text-stone-500">
          <p>No department data available</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-stone-900">{title}</h3>

        {/* Sort controls */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-stone-500">Sort by:</span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={sortBy === 'progress' ? 'primary' : 'secondary'}
              onClick={() => handleSortChange('progress')}
              className="text-xs px-2 py-1"
            >
              Progress {sortBy === 'progress' && <SortIcon className="w-3 h-3 ml-1" />}
            </Button>
            <Button
              size="sm"
              variant={sortBy === 'okrCount' ? 'primary' : 'secondary'}
              onClick={() => handleSortChange('okrCount')}
              className="text-xs px-2 py-1"
            >
              Count {sortBy === 'okrCount' && <SortIcon className="w-3 h-3 ml-1" />}
            </Button>
            <Button
              size="sm"
              variant={sortBy === 'alphabetical' ? 'primary' : 'secondary'}
              onClick={() => handleSortChange('alphabetical')}
              className="text-xs px-2 py-1"
            >
              A-Z {sortBy === 'alphabetical' && <SortIcon className="w-3 h-3 ml-1" />}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={sortedData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              type="category"
              dataKey="department"
              width={120}
              tick={{ fontSize: 12 }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="averageProgress"
              radius={[0, 4, 4, 0]}
              onClick={(bar) => onDepartmentClick?.((bar.payload as DepartmentProgress).departmentId)}
              style={{ cursor: onDepartmentClick ? 'pointer' : 'default' }}
            >
              {sortedData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getProgressColor(entry.averageProgress)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* An OKR counts in each of its owner's departments, so the bars do not partition the
          Total OKRs card beside them. */}
      <p className="text-xs text-stone-500 mt-2">
        An OKR counts in each of its owner's departments, so counts can add up to more than the
        total.
      </p>

      {/* Legend */}
      <div className="flex justify-center gap-6 mt-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#22c55e' }} />
          <span className="text-stone-600">On Track (70%+)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#f59e0b' }} />
          <span className="text-stone-600">At Risk (40-70%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#ef4444' }} />
          <span className="text-stone-600">Off Track (&lt;40%)</span>
        </div>
      </div>
    </Card>
  );
};

export default DepartmentProgressChart;
